"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 37: PAL-ZZ, the Nintendo triple cart
 * ------------------------------------------------------------------ *
 * "Super Mario Bros. + Tetris + Nintendo World Cup" (Europe) is three
 * MMC3 games in one pak, walled off from each other by a single outer
 * bank register in the MMC3's PRG-RAM window:
 *
 *   $6000-$7FFF   xxxx xQBB
 *                       │││
 *                       │└┴── Q0,Q1: both set forces PRG A16 high
 *                       └──── Q2: PRG ROM A17 and CHR ROM A17
 *
 * which gives three 64 KB cells plus one 128 KB one:
 *
 *   value  PRG window              CHR window
 *   0,1,2  $00000-$0FFFF (64 KB)   $00000-$1FFFF
 *   3      $10000-$1FFFF (64 KB)   $00000-$1FFFF
 *   4,5,6  $20000-$3FFFF (128 KB)  $20000-$3FFFF
 *   7      $30000-$3FFFF (64 KB)   $20000-$3FFFF
 *
 * The board is the MMC3 with two of its address lines replaced:
 *
 *   PRG A17 = Q2
 *   PRG A16 = (Q0 and Q1) or (Q2 and M16)   where M16 = the MMC3's A16
 *   CHR A17 = Q2
 *
 * so it is the MMC3's own banking with a translation applied to every
 * bank number the MMC3 hands the cartridge, rather than a second copy of
 * its tables. Translating in the two bank-loading helpers is that
 * translation, and it gets the hardwired $C000/$E000 windows right for
 * free: their bank numbers are already the ROM's second-to-last and last
 * 8 KB banks, whose low three bits are exactly what the board forwards.
 *
 * Two details of the register itself. It *is* the PRG-RAM window -- the
 * board carries no RAM -- so a write there belongs to the latch and goes
 * nowhere else, and since the MMC3 believes it is driving its RAM, the
 * $A001 write protect bit gates the write. Unlike RAM, the latch cannot be
 * read back.
 *
 * The latch is cleared by the CIC's reset line, so the Reset button drops
 * the player back on the multicart menu; that is modelled by clearing it
 * in reset().
 *
 * Used by Super Mario Bros. + Tetris + Nintendo World Cup (Europe) only.
 * See https://www.nesdev.org/wiki/INES_Mapper_037
 */

function register(Mappers) {
  Mappers[37] = function (nes) {
    // The MMC3 constructor sets up command, bankSelect and the IRQ state.
    Mappers[4].call(this, nes);

    // Outer bank select: the latched QBB value, 0-7.
    this.outerBank = 0;
  };

  // Inherit from MMC3 (which inherits from NROM).
  Mappers[37].prototype = new Mappers[4]();

  Mappers[37].prototype.write = function (address, value) {
    if (address >= 0x6000 && address < 0x8000) {
      // The MMC3 sees this window as its PRG-RAM, so its write protect
      // ($A001 bit 1) gates the latch exactly as it would gate a RAM write.
      // Nothing is stored: there is no RAM here, and the latch is not
      // readable.
      if (this.prgRamWritable) {
        this.setOuterBank(value & 0x07);
      }
      return;
    }

    Mappers[4].prototype.write.apply(this, arguments);
  };

  Mappers[37].prototype.setOuterBank = function (value) {
    if (value === this.outerBank) {
      return;
    }

    this.outerBank = value;

    // Every window in both address spaces moved at once.
    this.updatePrgBanks();
    this.updateChrBanks();
  };

  // An 8 KB PRG bank number from the MMC3, translated into the ROM.
  Mappers[37].prototype.translatePrgBank = function (bank8k) {
    var q = this.outerBank & 0x07;

    // Q2 is PRG A17, which is bit 4 of an 8 KB bank number.
    var outer = (q & 0x04) << 2;

    // A16 is forced high when both Q0 and Q1 are set. Otherwise it is the
    // MMC3's own A16 -- bit 3 of the bank number -- but only while Q2 is
    // set: that gate is what makes 0-3 a 64 KB cell, holding A16 low so the
    // MMC3's upper bank numbers fold back onto the same eight banks, and
    // 4-6 a 128 KB one where all sixteen are reachable.
    var a16;
    if ((q & 0x03) === 0x03) {
      a16 = 0x08;
    } else if ((q & 0x04) !== 0) {
      a16 = bank8k & 0x08;
    } else {
      a16 = 0;
    }

    // A15-A13 come straight from the MMC3; its A18-A20 reach nothing.
    return outer + a16 + (bank8k & 0x07);
  };

  // A 1 KB CHR bank number from the MMC3, translated into the ROM.
  Mappers[37].prototype.translateChrBank = function (bank1k) {
    // CHR A17 is Q2, so the outer register picks one of two 128 KB halves
    // (128 banks of 1 KB) and the MMC3's own A17 never reaches the ROM.
    return ((this.outerBank & 0x04) << 5) + (bank1k & 0x7f);
  };

  // Every cartridge window is loaded through these two helpers, so the
  // translation goes here rather than in a copy of updatePrgBanks() /
  // updateChrBanks(). The MMC3's loadCHRROM() uses loadVromBank() and is
  // deliberately not touched: it fills the pattern tables with the head of
  // the CHR ROM before any window is derived.
  Mappers[37].prototype.load8kRomBank = function (bank8k, address) {
    Mappers[4].prototype.load8kRomBank.call(
      this,
      this.translatePrgBank(bank8k),
      address,
    );
  };

  Mappers[37].prototype.load1kVromBank = function (bank1k, address) {
    Mappers[4].prototype.load1kVromBank.call(
      this,
      this.translateChrBank(bank1k),
      address,
    );
  };

  Mappers[37].prototype.reset = function () {
    Mappers[4].prototype.reset.apply(this);

    // The outer latch is cleared by the cartridge's reset line, so a soft
    // reset goes back to the menu. The windows have to be re-derived here:
    // clearing the latch without re-mapping would leave the ROM windows
    // pointing at the banks the old value selected.
    this.outerBank = 0;
    this.updatePrgBanks();
    this.updateChrBanks();
  };

  Mappers[37].prototype.toJSON = function () {
    var s = Mappers[4].prototype.toJSON.apply(this);
    s.outerBank = this.outerBank;
    return s;
  };

  Mappers[37].prototype.fromJSON = function (s) {
    // The latch has to be back before the MMC3's restore re-derives the
    // windows from it, or they would be mapped for the old value.
    this.outerBank = (s.outerBank || 0) & 0x07;
    Mappers[4].prototype.fromJSON.apply(this, arguments);
  };
}

module.exports = { register: register };
